import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import type { StaffPermissions } from '@structural-climbing/auth'
import { Button, Checkbox } from '@structural-climbing/ui'
import { useGymContext } from '../../contexts/GymContext'
import { tierIncludesCompetitions } from '../../lib/tiers'
import { isPermissionOffered } from './staffTitles'
import { GymDialog } from '../../components/GymDialog'

interface PermissionsDialogProps {
  staffId: string
  staffName: string
  permissions: StaffPermissions
  onClose: () => void
  onSaved: (staffId: string, permissions: StaffPermissions) => void
}

export function PermissionsDialog({
  staffId,
  staffName,
  permissions,
  onClose,
  onSaved,
}: PermissionsDialogProps) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const { planTier, productsEnabled } = useGymContext()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const PERMISSION_ITEMS: { key: keyof StaffPermissions; label: string }[] = (
    [
      { key: 'dashboard', label: t('team.permDashboard') },
      { key: 'check_in', label: t('team.permCheckIn') },
      { key: 'my_members', label: t('team.permMyMembers') },
      { key: 'members', label: t('team.permMembers') },
      { key: 'requests', label: t('team.permRequests') },
      { key: 'plans', label: t('team.permPlans') },
      { key: 'subscriptions', label: t('team.permSubscriptions') },
      { key: 'routes', label: t('team.permRoutes') },
      { key: 'competitions', label: t('team.permCompetitions') },
      { key: 'products', label: t('team.permProducts') },
      { key: 'calendar', label: t('team.permCalendar') },
      { key: 'news', label: t('team.permNews') },
      { key: 'statistics', label: t('team.permStatistics') },
      { key: 'invoices', label: t('team.permInvoices') },
      { key: 'exports', label: t('team.permExports') },
    ] as { key: keyof StaffPermissions; label: string }[]
  ).filter(
    // Nothing to grant when the plan has no competitions: the page is hidden
    // and the endpoints refuse, so the toggle would only confuse.
    // The same goes for Products in a gym that does not have the page.
    (item) =>
      isPermissionOffered(item.key, {
        productsEnabled,
        competitionsEnabled: tierIncludesCompetitions(planTier),
      }),
  )

  const [local, setLocal] = useState<StaffPermissions>({ ...permissions })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function toggle(key: keyof StaffPermissions) {
    setLocal((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  async function handleSave() {
    setLoading(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const resp = await fetch(`${apiUrl}/staff/${staffId}/permissions`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ permissions: local }),
      })
      if (!resp.ok) {
        const { error: msg } = (await resp.json()) as { error: string }
        throw new Error(msg ?? t('team.errorSavePermissions'))
      }
      onSaved(staffId, local)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('team.errorSavePermissions'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <GymDialog onClose={onClose} size="sm">
      <div className="p-6">
        <h2 className="mb-1 text-base font-semibold">{t('team.permissionsTitle')}</h2>
        <p className="mb-5 text-sm text-foreground/60">{staffName}</p>
        <div className="flex flex-col gap-3">
          {PERMISSION_ITEMS.map(({ key, label }) => (
            <Checkbox key={key} isSelected={local[key] === true} onValueChange={() => toggle(key)}>
              {label}
            </Checkbox>
          ))}
        </div>
        {error && <p className="mt-4 text-sm text-danger">{error}</p>}
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="flat" onClick={onClose} isDisabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button color="primary" onClick={handleSave} isLoading={loading}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </GymDialog>
  )
}
